import {getCurrencyISOCode} from '@stripe-internal/cldr';
import handleGlobalizeError from 'src/internal/errors/handleGlobalizeError/handleGlobalizeError';

import type {IReports} from '@sail/observability';
import type {CldrFactory, CldrStatic} from 'cldrjs';
import type Globalize from 'globalize';
import type {FormatDisplayNameType} from 'src/public/formatDisplayName/types';

const getCldrInstance = (
  globalize: Globalize.Shared,
  locale: string,
): CldrStatic => {
  const Cldr = globalize.cldr.constructor as CldrFactory;
  const cldr = new Cldr(locale);

  return cldr;
};

function getCldrRawDisplayNameValue(
  globalize: Globalize.Shared,
  type: FormatDisplayNameType,
  userValue: string,
): string {
  const value = String(userValue);
  const cldr = getCldrInstance(globalize, value);
  const langScript = [cldr.attributes.language, cldr.attributes.script].join(
    '-',
  );
  const isFallbackLocale = !value
    .toLowerCase()
    .startsWith(cldr.attributes.language.toLowerCase());

  let rawValue: string | undefined;

  if (type === 'currency') {
    const currencyCode = getCurrencyISOCode(value);

    rawValue = globalize.cldr.main(
      `numbers/currencies/${currencyCode}/displayName`,
    );
  } else if (type === 'region') {
    rawValue =
      globalize.cldr.main(
        `localeDisplayNames/territories/${value.toUpperCase()}`,
      ) || globalize.cldr.main(`localeDisplayNames/territories/${value}`);
  } else if (type === 'language') {
    rawValue =
      globalize.cldr.main(
        `localeDisplayNames/languages/${value.toLowerCase()}`,
      ) || globalize.cldr.main(`localeDisplayNames/languages/${langScript}`);
  }

  if (!rawValue && !isFallbackLocale) {
    rawValue = getCldrRawDisplayNameValue(
      globalize,
      type,
      getValueFromLocale(cldr, type) || '',
    );
  }

  return rawValue || '';
}

function getValueFromLocale(
  cldr: CldrStatic,
  type: FormatDisplayNameType,
): string | undefined {
  if (type === 'language') {
    return cldr.attributes.language;
  }

  if (type === 'region') {
    return cldr.attributes.territory;
  }

  return undefined;
}

export default function getCldrDisplayNameValue(
  globalize: Globalize.Shared,
  type: FormatDisplayNameType,
  value: string,
  reports: IReports,
): string {
  try {
    return getCldrRawDisplayNameValue(globalize, type, value);
  } catch (error) {
    handleGlobalizeError(error, reports);
  }
}
